import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Typography, Card, CardContent, Chip, Stack, Button, Skeleton,
  Alert, ToggleButton, ToggleButtonGroup,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ReplayIcon from '@mui/icons-material/Replay';
import Layout from '../components/Layout';
import { fetchFeedback, updateFeedbackStatus, FeedbackEntry, apiErrorText } from '../api/client';

function fmtDateTime(s: string): string {
  try { return new Date(s).toLocaleString('de-DE'); } catch { return s; }
}

export default function FeedbackPage() {
  const [entries, setEntries] = useState<FeedbackEntry[]>([]);
  const [filter, setFilter] = useState<'offen' | 'erledigt' | 'alle'>('offen');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setEntries(await fetchFeedback(filter));
      setError('');
    } catch (e: any) {
      setError(apiErrorText(e) || 'Feedback konnte nicht geladen werden');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const setStatus = async (f: FeedbackEntry, status: 'offen' | 'erledigt') => {
    await updateFeedbackStatus(f.id, status);
    load();
  };

  return (
    <Layout>
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" fontWeight={700} gutterBottom>Feedback</Typography>
          <Typography variant="body2" color="text.secondary">
            Rückmeldungen aus dem „Was funktioniert nicht?"-Button — von Köchen und Admins.
          </Typography>
        </Box>
        <ToggleButtonGroup size="small" exclusive value={filter}
          onChange={(_, v) => v && setFilter(v)}>
          <ToggleButton value="offen">Offen</ToggleButton>
          <ToggleButton value="erledigt">Erledigt</ToggleButton>
          <ToggleButton value="alle">Alle</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading ? <Skeleton variant="rounded" height={200} /> : entries.length === 0 ? (
        <Alert severity="success">Keine Einträge.</Alert>
      ) : (
        <Stack spacing={1.5}>
          {entries.map(f => (
            <Card key={f.id} variant="outlined">
              <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ mb: 0.5 }}>
                  <Chip size="small"
                    color={f.status === 'offen' ? 'warning' : 'success'}
                    label={f.status === 'offen' ? 'Offen' : 'Erledigt'} />
                  <Typography variant="caption" color="text.secondary">
                    {fmtDateTime(f.created_at)} · {f.user_email || 'unbekannt'}
                    {f.role ? ' (' + f.role + ')' : ''}{f.page ? ' · Seite: ' + f.page : ''}
                  </Typography>
                </Stack>
                <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>{f.message}</Typography>
                <Box sx={{ mt: 1 }}>
                  {f.status === 'offen' ? (
                    <Button size="small" startIcon={<CheckCircleIcon />} onClick={() => setStatus(f, 'erledigt')}>
                      Als erledigt markieren
                    </Button>
                  ) : (
                    <Button size="small" startIcon={<ReplayIcon />} onClick={() => setStatus(f, 'offen')}>
                      Wieder öffnen
                    </Button>
                  )}
                </Box>
              </CardContent>
            </Card>
          ))}
        </Stack>
      )}
    </Layout>
  );
}
